import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import "./RawMaterialStockDashboard.scss";

interface TradingProductPosAllocation {
    posId: number; posName: string; stock: number; availableStock: number;
}
interface TradingAllocatedProduct {
    productId: number; productName: string; stock: number;
    availableStock: number; stockReductionFactor: number;
    unitPrice: number;
    posAllocations: TradingProductPosAllocation[];
}
interface TradingBatch {
    batchId: number; sourceTypeId: number; sourceTypeName: string;
    batchStock: number; batchAvailableStock: number; batchAllocatedStock: number;
    batchInPosAvailableStock: number;
    unitPrice: number;
    stockReductionFactor: number;
    rawMaterialPurchaseId?: number;
    allocatedProducts: TradingAllocatedProduct[];
}
interface TradingRawMaterialStock {
    rawMaterialId: number; rawMaterialName: string;
    measurementUnit?: string;
    totalPurchasedQty: number; remainingStock: number; remainingStockValue: number;
    totalBatchStock: number; totalAvailableStock: number; totalAllocatedStock: number;
    totalInPosAvailableStock: number;
    totalYetToInwardValue: number;
    totalInPosValue: number;
    batches: TradingBatch[];
}

interface FlatRow {
    rawMaterialId: number;
    rawMaterialName: string;
    unit: string;
    measurementUnit?: string;
    rmStock: number;
    rmStockValue: number;
    batchAvailableStock: number;
    yetToInwardValue: number;
    posStock: number;
    posValue: number;
    tradingBatches: TradingBatch[];
}

const f = (n: number) => n.toFixed(2);

// Returns true if the raw material is a seed/crop input (measured in KG)
const isSeedItem = (name: string): boolean => {
    const lower = name.toLowerCase();
    return /(seed|விதை|groundnut|castor|sesame|sunflower|soybean|cotton|mustard|flax|linseed|niger|safflower)/i.test(lower);
};

// ── Shared Tooltip Component with Pagination ──
const SmartTooltip: React.FC<{
    title: string;
    value: number;
    unit: string;
    items: any[];
    renderItem: (item: any, idx: number) => React.ReactNode;
}> = ({ title, value, unit, items, renderItem }) => {
    const [page, setPage] = useState(0);
    const pageSize = 5;
    const totalPages = Math.ceil(items.length / pageSize);
    const visibleItems = items.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <div className="custom-tt" onClick={(e) => e.stopPropagation()}>
            <div className="custom-tt-inner">
                <div className="tt-bp">
                    <div className="tt-bp-name">
                        {title}{' '}
                        <span className={`tt-bp-val ${value < 0 ? 'neg' : ''}`}>
                            {value < 0 && '-'} {f(Math.abs(value))} {unit}
                        </span>
                    </div>

                    <div className="tt-allocs">
                        {visibleItems.map(renderItem)}
                    </div>

                    {totalPages > 1 && (
                        <div className="tt-pager">
                            <button 
                                className="tt-pager-btn" 
                                disabled={page === 0} 
                                onClick={(e) => { e.preventDefault(); setPage(p => p - 1); }}
                            >
                                <i className="pi pi-chevron-left"></i>
                            </button>
                            <span className="tt-pager-info">{page + 1} / {totalPages}</span>
                            <button 
                                className="tt-pager-btn" 
                                disabled={page === totalPages - 1} 
                                onClick={(e) => { e.preventDefault(); setPage(p => p + 1); }}
                            >
                                <i className="pi pi-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TradingStockDashboard: React.FC = () => {
    const [allRows, setAllRows] = useState<FlatRow[]>([]);
    const [selectedRawMaterials, setSelectedRawMaterials] = useState<string[]>([]);
    const [posOptions, setPosOptions] = useState<{ label: string; value: number }[]>([]);
    const [selectedPosIds, setSelectedPosIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [first, setFirst] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    useEffect(() => { fetchMUPlants(); }, []);
    useEffect(() => {
        if (selectedPosIds.length > 0) fetchStockData();
        else setAllRows([]);
    }, [selectedPosIds]);

    const fetchMUPlants = async () => {
        try {
            const res = await oilProductionService.GetMUPlantName();
            if (res && Array.isArray(res)) {
                const opts = res.map((mu: any) => ({
                    label: mu.address2 ?? mu.posName ?? `Unit-${mu.posID}`,
                    value: Number(mu.posID),
                }));
                setPosOptions(opts);
                setSelectedPosIds(opts.map((o: any) => o.value));
            } else {
                setPosOptions([]);
                setSelectedPosIds([]);
            }
        } finally { setInitialLoading(false); }
    };

    const fetchStockData = async () => {
        try {
            setLoading(true);
            const res: TradingRawMaterialStock[] = await oilProductionService.GetTradingRawMaterialStock(selectedPosIds);
            const flat: FlatRow[] = (Array.isArray(res) ? res : []).map(rm => {
                const isSeed = isSeedItem(rm.rawMaterialName);
                let posStockVal = 0;
                if (rm.batches) {
                    rm.batches.forEach(b => {
                        if (b.allocatedProducts) {
                            b.allocatedProducts.forEach(ap => {
                                posStockVal += isSeed ? (ap.availableStock * ap.stockReductionFactor) : ap.availableStock;
                            });
                        }
                    });
                }

                return {
                    rawMaterialId: rm.rawMaterialId,
                    rawMaterialName: rm.rawMaterialName,
                    unit: isSeed ? 'KG' : 'Pcs',
                    measurementUnit: rm.measurementUnit || (isSeed ? 'KG' : 'Pcs'),
                    rmStock: rm.remainingStock,
                    rmStockValue: rm.remainingStockValue || 0,
                    batchAvailableStock: rm.totalAvailableStock,
                    yetToInwardValue: rm.totalYetToInwardValue || 0,
                    posStock: posStockVal,
                    posValue: rm.totalInPosValue || 0,
                    tradingBatches: rm.batches || []
                };
            }).sort((a, b) => b.rmStock - a.rmStock);

            setAllRows(flat);
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    };

    const displayRows = React.useMemo(() => {
        setFirst(0); // reset to page 1 when filter changes
        let filtered = allRows;
        if (selectedRawMaterials && selectedRawMaterials.length > 0) {
            filtered = filtered.filter(r => selectedRawMaterials.includes(r.rawMaterialName));
        }
        return filtered;
    }, [allRows, selectedRawMaterials]);

    const rmOptions = React.useMemo(() => {
        const uniqueNames = Array.from(new Set(allRows.map(r => r.rawMaterialName)));
        return uniqueNames.map(name => ({ label: name, value: name })).sort((a, b) => a.label.localeCompare(b.label));
    }, [allRows]);

    const rmHeader = (
        <>
            <span className="rm-header-label">RAW MATERIAL</span>
            <div className="rm-header-filter-container">
                <MultiSelect
                    value={selectedRawMaterials}
                    options={rmOptions}
                    onChange={(e) => setSelectedRawMaterials(e.value)}
                    placeholder="Search Raw Material"
                    filter
                    maxSelectedLabels={1}
                    className="rm-header-filter"
                />
            </div>
        </>
    );

    const remainingBody = (r: FlatRow) => {
        const val = r.rmStock;
        const valAmt = r.rmStockValue;
        const sev = val > 20 ? 'success' : val > 0 ? 'warning' : 'danger';
        const fv = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: n < 1000 ? 2 : 0 });
        const unit = r.measurementUnit || r.unit;

        // Build tooltip items: show raw material name + stock + value
        // Exactly like WIP Dashboard (shows RM name, not Batch #)
        const tooltipItems = val !== 0
            ? [{ name: r.rawMaterialName, stock: r.rmStock, value: r.rmStockValue }]
            : [];

        return (
            <div className="hover-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <Tag value={`${f(val)} ${unit}`} severity={sev} className="compact-tag" />
                {valAmt > 0 && (
                    <span className="stock-value-badge">
                        <span style={{ fontSize: '0.85em', marginRight: '2px' }}>₹</span>{fv(valAmt)}
                    </span>
                )}
                {tooltipItems.length > 0 && (
                    <SmartTooltip
                        title="Raw Materials"
                        value={val}
                        unit={unit}
                        items={tooltipItems}
                        renderItem={(rm, idx) => (
                            <div key={idx} className="tt-ap">
                                <div className="tt-ap-name">
                                    <div className="tt-ap-lbl">
                                        <i className="pi pi-angle-right"></i> {rm.name}
                                    </div>
                                    <span className="tt-ap-avail">{f(rm.stock)} <small>{unit}</small></span>
                                </div>
                                {rm.value > 0 && (
                                    <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#86efac', marginTop: '2px' }}>
                                        ₹{fv(rm.value)}
                                    </div>
                                )}
                            </div>
                        )}
                    />
                )}
            </div>
        );
    };

    const hoverCell = (row: FlatRow, field: 'batchAvailableStock' | 'posStock') => {
        const unit = row.unit;
        const displayVal = row[field];

        if (field === 'batchAvailableStock' && row.tradingBatches && row.tradingBatches.length > 0) {
            const validBatches = row.tradingBatches.filter(b => b.batchAvailableStock > 0);
            const batchesSum = validBatches.reduce((sum, b) => sum + b.batchAvailableStock, 0);
            const valueAmt = row.yetToInwardValue;
            const fv = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: n < 1000 ? 2 : 0 });

            return (
                <div className={`hover-wrapper ${batchesSum < 0 ? 'neg' : ''}`}>
                    <span className="hover-val">
                        {batchesSum === 0 ? '—' : <>{batchesSum < 0 && '-'} {f(Math.abs(batchesSum))} <small>{unit}</small></>}
                        {batchesSum !== 0 && <i className="pi pi-info-circle"></i>}
                    </span>
                    {valueAmt > 0 && (
                        <span className="stock-value-badge">
                            <span style={{ fontSize: '0.85em', marginRight: '2px' }}>₹</span>{fv(valueAmt)}
                        </span>
                    )}
                    {batchesSum !== 0 && (
                        <SmartTooltip 
                            title={`${row.rawMaterialName} Batches`}
                            value={batchesSum}
                            unit={unit}
                            items={validBatches}
                            renderItem={(b) => (
                                <div key={b.batchId} className="tt-ap">
                                    <div className="tt-ap-name">
                                        <div className="tt-ap-lbl">
                                            <i className="pi pi-angle-right"></i> Batch #{b.batchId} <small>({b.sourceTypeName})</small>
                                        </div>
                                        <span className="tt-ap-avail">{f(b.batchAvailableStock)} <small>Avail.</small></span>
                                    </div>
                                    <div className="tt-pos-tags" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span className="tt-pos-tag" style={{background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '0.65rem', padding: '0'}}>
                                            Total Stock: {f(b.batchStock)} {unit}
                                        </span>
                                        {b.batchAvailableStock * b.unitPrice > 0 && (
                                            <span style={{ color: '#86efac', fontSize: '0.7rem', fontWeight: 600 }}>
                                                <span style={{ fontSize: '0.85em', marginRight: '1px' }}>₹</span>{fv(b.batchAvailableStock * b.stockReductionFactor * b.unitPrice)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        />
                    )}
                </div>
            );
        } else if (field === 'posStock' && row.tradingBatches && row.tradingBatches.length > 0) {
            const productMap = new Map<number, TradingAllocatedProduct>();
            row.tradingBatches.forEach(b => {
                (b.allocatedProducts ?? []).forEach(ap => {
                    if (!productMap.has(ap.productId)) {
                        productMap.set(ap.productId, JSON.parse(JSON.stringify(ap)));
                    } else {
                        const existing = productMap.get(ap.productId)!;
                        existing.availableStock += ap.availableStock;
                        existing.stock += ap.stock;
                        (ap.posAllocations ?? []).forEach(pos => {
                            const existingPos = existing.posAllocations.find(p => p.posId === pos.posId);
                            if (existingPos) {
                                existingPos.availableStock += pos.availableStock;
                                existingPos.stock += pos.stock;
                            } else {
                                existing.posAllocations.push({ ...pos });
                            }
                        });
                    }
                });
            });
            const mergedProducts = Array.from(productMap.values()).filter(ap => ap.availableStock > 0);
            const valueAmt = row.posValue;
            const fv = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: n < 1000 ? 2 : 0 });

            return (
                <div className={`hover-wrapper ${displayVal < 0 ? 'neg' : ''}`}>
                    <span className="hover-val">
                        {displayVal === 0 ? '—' : <>{displayVal < 0 && '-'} {f(Math.abs(displayVal))} <small>{unit}</small></>}
                        {displayVal !== 0 && <i className="pi pi-info-circle"></i>}
                    </span>
                    {valueAmt > 0 && (
                        <span className="stock-value-badge">
                            <span style={{ fontSize: '0.85em', marginRight: '2px' }}>₹</span>{fv(valueAmt)}
                        </span>
                    )}
                    {displayVal !== 0 && mergedProducts.length > 0 && (
                        <SmartTooltip 
                            title={`${row.rawMaterialName} Products`}
                            value={displayVal}
                            unit={unit}
                            items={mergedProducts}
                            renderItem={(ap: TradingAllocatedProduct) => {
                                const validPos = ap.posAllocations?.filter((p: TradingProductPosAllocation) => p.availableStock > 0);
                                return (
                                    <div key={ap.productId} className="tt-ap">
                                        <div className="tt-ap-name">
                                            <div className="tt-ap-lbl">
                                                <i className="pi pi-angle-right"></i> {ap.productName}
                                            </div>
                                            <span className="tt-ap-avail">{f(ap.availableStock)} <small>Avail.</small></span>
                                        </div>
                                        {ap.availableStock * ap.unitPrice > 0 && (
                                            <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#86efac', marginBottom: '2px' }}>
                                                <span style={{ fontSize: '0.85em', marginRight: '1px' }}>₹</span>{fv(ap.availableStock * ap.unitPrice)}
                                            </div>
                                        )}
                                        {validPos && validPos.length > 0 && (
                                            <div className="tt-pos-tags">
                                                {validPos.map((pos: TradingProductPosAllocation) => (
                                                    <span key={pos.posId} className="tt-pos-tag">
                                                        <span className="tt-pos-n">{pos.posName}</span>
                                                        <span className="tt-pos-v">{f(pos.availableStock)}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    )}
                </div>
            );
        }

        return (
            <span className={`zero-val ${displayVal < 0 ? 'neg' : ''}`}>
                {displayVal === 0 ? '—' : <>{displayVal < 0 && '-'} {f(Math.abs(displayVal))} <small>{unit}</small></>}
            </span>
        );
    };

    const exportCSV = () => {
        const esc = (val: string | number | null | undefined) =>
            `"${String(val ?? '').replace(/"/g, '""')}"`;
        const fv = (n: number) => n.toFixed(2);

        // ── PASS 1: collect all unique POS names ──
        const allPosNames: string[] = [];
        const posNameSet = new Set<string>();

        displayRows.forEach(r => {
            const productMap = new Map<number, TradingAllocatedProduct>();
            r.tradingBatches.forEach(b => {
                (b.allocatedProducts ?? []).forEach(ap => {
                    if (!productMap.has(ap.productId)) {
                        productMap.set(ap.productId, JSON.parse(JSON.stringify(ap)));
                    } else {
                        const ex = productMap.get(ap.productId)!;
                        (ap.posAllocations ?? []).forEach(pa => {
                            const ep = ex.posAllocations.find(p => p.posId === pa.posId);
                            ep ? (ep.availableStock += pa.availableStock) : ex.posAllocations.push({ ...pa });
                        });
                    }
                });
            });
            Array.from(productMap.values()).forEach(ap => {
                (ap.posAllocations ?? []).filter(p => p.availableStock > 0).forEach(p => {
                    if (!posNameSet.has(p.posName)) {
                        posNameSet.add(p.posName);
                        allPosNames.push(p.posName);
                    }
                });
            });
        });

        // ── PASS 2: build headers and data rows ──
        const fixedHeaders = [
            "Raw Material", "RM Stock", "RM Stock Value (₹)",
            "Yet to Inward", "Yet to Inward Value (₹)",
            "In POS", "In POS Value (₹)",
            "Batch #", "Source Type", "Batch Total", "Batch Available", "Batch Unit Price",
            "Product", "Product Available", "Product Value (₹)",
            ...allPosNames,
            "Unit"
        ];

        const csvRows: string[] = [fixedHeaders.map(esc).join(',')];

        displayRows.forEach(r => {
            const unit = r.measurementUnit || r.unit || 'KG';
            const displayBatchAvailable = r.tradingBatches.reduce((sum, b) => sum + b.batchAvailableStock, 0);

            const productMap = new Map<number, TradingAllocatedProduct>();
            r.tradingBatches.forEach(b => {
                (b.allocatedProducts ?? []).forEach(ap => {
                    if (!productMap.has(ap.productId)) {
                        productMap.set(ap.productId, JSON.parse(JSON.stringify(ap)));
                    } else {
                        const ex = productMap.get(ap.productId)!;
                        ex.availableStock += ap.availableStock;
                        ex.stock += ap.stock;
                        (ap.posAllocations ?? []).forEach(pa => {
                            const ep = ex.posAllocations.find(p => p.posId === pa.posId);
                            ep ? (ep.availableStock += pa.availableStock) : ex.posAllocations.push({ ...pa });
                        });
                    }
                });
            });
            const mergedProducts = Array.from(productMap.values()).filter(ap => ap.availableStock > 0);

            if (r.tradingBatches.length === 0) {
                csvRows.push([
                    esc(r.rawMaterialName), esc(fv(r.rmStock)), esc(fv(r.rmStockValue)),
                    esc(fv(displayBatchAvailable)), esc(fv(r.yetToInwardValue)),
                    esc(fv(r.posStock)), esc(fv(r.posValue)),
                    esc('—'), esc('—'), esc('—'), esc('—'), esc('—'),
                    esc('—'), esc('—'), esc('—'),
                    ...allPosNames.map(() => esc('')),
                    esc(unit)
                ].join(','));
                return;
            }

            r.tradingBatches.forEach(b => {
                const batchProducts = b.allocatedProducts?.filter(ap => ap.availableStock > 0) ?? [];

                if (batchProducts.length === 0) {
                    csvRows.push([
                        esc(r.rawMaterialName), esc(fv(r.rmStock)), esc(fv(r.rmStockValue)),
                        esc(fv(displayBatchAvailable)), esc(fv(r.yetToInwardValue)),
                        esc(fv(r.posStock)), esc(fv(r.posValue)),
                        esc(`Batch #${b.batchId}`), esc(b.sourceTypeName), esc(fv(b.batchStock)), esc(fv(b.batchAvailableStock)), esc(fv(b.unitPrice)),
                        esc('—'), esc('—'), esc('—'),
                        ...allPosNames.map(() => esc('')),
                        esc(unit)
                    ].join(','));
                    return;
                }

                batchProducts.forEach(ap => {
                    const posMap = new Map<string, number>();
                    (ap.posAllocations ?? []).filter(p => p.availableStock > 0)
                        .forEach(p => posMap.set(p.posName, p.availableStock));
                    const productValue = ap.unitPrice > 0 ? ap.availableStock * ap.unitPrice : 0;

                    csvRows.push([
                        esc(r.rawMaterialName), esc(fv(r.rmStock)), esc(fv(r.rmStockValue)),
                        esc(fv(displayBatchAvailable)), esc(fv(r.yetToInwardValue)),
                        esc(fv(r.posStock)), esc(fv(r.posValue)),
                        esc(`Batch #${b.batchId}`), esc(b.sourceTypeName), esc(fv(b.batchStock)), esc(fv(b.batchAvailableStock)), esc(fv(b.unitPrice)),
                        esc(ap.productName), esc(fv(ap.availableStock)), esc(fv(productValue)),
                        ...allPosNames.map(n => esc(posMap.has(n) ? fv(posMap.get(n)!) : '')),
                        esc(unit)
                    ].join(','));
                });
            });
        });

        const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `WIP_Trading_${new Date().toISOString().split('T')[0]}.csv`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header={rmHeader} className="col-rm" style={{ width: '30%' }} sortable field="rawMaterialName" />
                <Column header={<span className="rm-header-label">AVAILABLE STOCK (RM)</span>} className="col-stock" style={{ width: '20%' }} sortable field="rmStock" />
                <Column header={<><div className="hdr-main">Yet to Inward</div><div className="hdr-sub">Batch Available</div></>} className="col-sub" style={{ width: '25%' }} sortable field="batchAvailableStock" />
                <Column header={<><div className="hdr-main">In POS</div><div className="hdr-sub">Product Available</div></>} className="col-sub" style={{ width: '25%' }} sortable field="posStock" />
            </Row>
        </ColumnGroup>
    );

    if (initialLoading) return (
        <div className="rmd-center-spin">
            <ProgressSpinner style={{ width: 44, height: 44 }} strokeWidth="3" animationDuration=".5s" />
        </div>
    );

    return (
        <div className="rmd-root">
            <div className="rmd-header">
                <div className="rmd-header__left">
                    <div className="rmd-header__icon"><i className="pi pi-truck"></i></div>
                    <div>
                        <h1 className="rmd-header__title">Work In-Progress Trading</h1>
                        <p className="rmd-header__sub">Trading Batches · Materials · POS Allocations</p>
                    </div>
                </div>
                <div className="rmd-filter">
                    <label className="rmd-filter__label">Manufacturing Unit</label>
                    <MultiSelect
                        value={selectedPosIds} options={posOptions}
                        onChange={e => setSelectedPosIds(e.value)}
                        placeholder="Select Units" maxSelectedLabels={3} display="chip"
                        className="rmd-multiselect"
                    />
                    <FileActionButton action="export"
                        icon="pi pi-file-excel"
                        label="Export"
                        severity="success"
                        outlined
                        onClick={exportCSV}
                        disabled={displayRows.length === 0}
                        style={{ padding: '0.4rem 1rem', fontSize: '0.8rem', marginLeft: '0.5rem', borderRadius: '8px' }}
                    />
                </div>
            </div>

            <div className="rmd-content">
                <div className="rmd-table-card">
                    <DataTable
                        value={displayRows}
                        loading={loading}
                        headerColumnGroup={headerGroup}
                        dataKey="rawMaterialId"
                        className="rmd-datatable p-datatable-sm"
                        emptyMessage="No data for selected units."
                        stripedRows
                        rowHover
                        rowClassName={(data: FlatRow) => {
                            const globalIdx = displayRows.findIndex(r => r.rawMaterialId === data.rawMaterialId);
                            const pageIdx = globalIdx % rowsPerPage;
                            const rowsOnCurrentPage = Math.min(rowsPerPage, displayRows.length - Math.floor(globalIdx / rowsPerPage) * rowsPerPage);
                            const isBottom = (pageIdx >= rowsOnCurrentPage - 3 || globalIdx >= displayRows.length - 3) &&
                                             pageIdx >= 3 &&
                                             displayRows.length >= 6;
                            return {
                                'row-bottom': isBottom
                            };
                        }}
                        paginator
                        first={first}
                        onPage={(e) => { setFirst(e.first); setRowsPerPage(e.rows); }}
                        rows={rowsPerPage}
                        rowsPerPageOptions={[15, 30, 50]}
                        tableStyle={{ width: '100%', tableLayout: 'auto' }}
                        showGridlines
                    >
                        <Column field="rawMaterialName" sortable bodyClassName="rm-name-cell"
                            body={(r: FlatRow) => (
                                <div className="bp-name-wrapper">
                                    <i className="pi pi-box" style={{ color: '#0ea5e9', marginRight: '8px', fontSize: '0.9rem' }}></i>
                                    {r.rawMaterialName}
                                </div>
                            )} />
                        <Column field="rmStock" sortable align="right" body={remainingBody} />
                        <Column field="batchAvailableStock" sortable align="right" bodyClassName="val-cell"
                            body={(r: FlatRow) => hoverCell(r, 'batchAvailableStock')} />
                        <Column field="posStock" sortable align="right" bodyClassName="val-cell"
                            body={(r: FlatRow) => hoverCell(r, 'posStock')} />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default TradingStockDashboard;
